import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import useResizeObserver from 'use-resize-observer';
import { Box } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors } from '@99designs/emu/settings';
import { IconDocument1, IconTrash2 } from '@99designs/icons';
import { BLANKSLATE_ICON_SIZE, BlankSlate } from '@99designs/spa-common';
import { useViewer } from '@99designs/viewer';
import { AnnotationContext } from '../AnnotationContext';
import { useFileOverlayContext } from '../FileOverlayContext';
import { ScribbleContext } from '../ScribbleContext/ScribbleContext';
import { SelectedEventContext } from '../SelectedEventContext';
import { File } from '../types';
import AnnotationCanvas from './AnnotationCanvas';
import Artboard from './Artboard';
import { FeedbackHandler } from './FeedbackHandler';
import Image from './Image';
import ScribbleLayer, { Path } from './ScribbleLayer';
import ScrollPane from './ScrollPane';
import calculatePosition from './calculatePosition';
import { calculateScale } from './calculateScale';

interface FilePaneProps<A, S> {
    file: File;
    useSelectedEvent: SelectedEventContext['useSelectedEvent'];
    useAnnotation: AnnotationContext<A>['useAnnotation'];
    useScribble: ScribbleContext<S>['useScribble'];
}

export const FilePaneWrapper = styled.div`
    width: 100%;
    height: 100%;

    ${mediaQuery.between('small', 'medium')} {
        min-height: 100vw;
    }
`;

export const FilePaneBlankSlate = () => {
    const { setViewSize } = useFileOverlayContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => setViewSize({ width: 0, height: 0 }), []);
    return (
        <Box marginTop="1.5bu">
            <BlankSlate
                icon={
                    <IconDocument1
                        color={colors.grey300}
                        height={BLANKSLATE_ICON_SIZE}
                        width={BLANKSLATE_ICON_SIZE}
                        data-testid="Document1"
                    />
                }
            />
        </Box>
    );
};

export const FilePaneDeletedSlate = () => {
    const { setViewSize } = useFileOverlayContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => setViewSize({ width: 0, height: 0 }), []);
    return (
        <Box marginTop="1.5bu">
            <BlankSlate
                heading={__('File deleted')}
                icon={
                    <Box marginBottom="2bu">
                        <IconTrash2
                            color={colors.grey300}
                            height={BLANKSLATE_ICON_SIZE}
                            width={BLANKSLATE_ICON_SIZE}
                        />
                    </Box>
                }
            />
        </Box>
    );
};

function FilePane<A, S>({
    file,
    useSelectedEvent,
    useAnnotation,
    useScribble,
}: FilePaneProps<A, S>) {
    if (file.isDeleted && !file.previewUrl) {
        return <FilePaneDeletedSlate />;
    }
    if (!file.width || !file.previewUrl) {
        return <FilePaneBlankSlate />;
    }

    // FIXME: Rule of hooks violation https://gitlab.com/vistaprint-org/99designs/frontend/-/issues/3523
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const viewer = useViewer();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { viewSize, setViewSize, zoomLevel } = useFileOverlayContext();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [ref, width, height] = useResizeObserver();
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { currentPath, scribble } = useScribble();

    // FIXME: Rule of hooks violation https://gitlab.com/vistaprint-org/99designs/frontend/-/issues/3523
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        setViewSize({ width, height });
    }, [width, height, setViewSize]);

    const imageSize = {
        width: file.width,
        height: file.height,
    };

    const scale = calculateScale(imageSize, viewSize, zoomLevel);
    const { artboardSize, artboardOrigin, scaledImageSize, imageToScreen, pointerPosition } =
        calculatePosition(imageSize, viewSize, scale);

    // FIXME: Rule of hooks violation https://gitlab.com/vistaprint-org/99designs/frontend/-/issues/3523
    // eslint-disable-next-line react-hooks/rules-of-hooks, react-hooks/exhaustive-deps
    const memoPointerPosition = useCallback(pointerPosition, [
        viewSize,
        zoomLevel,
        imageSize.width,
        imageSize.height,
    ]);

    return (
        <FilePaneWrapper ref={ref as React.RefObject<HTMLDivElement>}>
            <ScrollPane viewSize={viewSize} scaledImageSize={scaledImageSize}>
                <Artboard artboardSize={artboardSize}>
                    <FeedbackHandler
                        pointerPosition={memoPointerPosition}
                        useSelectedEvent={useSelectedEvent}
                        useAnnotation={useAnnotation}
                        useScribble={useScribble}
                        fileId={file.id}
                    >
                        <Image
                            previewUrl={file.previewUrl}
                            imageSize={imageSize}
                            artboardOrigin={artboardOrigin}
                            scale={scale}
                            artboardSize={artboardSize}
                        />
                        {viewer && file.viewerCanAnnotate && (
                            <AnnotationCanvas
                                useSelectedEvent={useSelectedEvent}
                                useAnnotation={useAnnotation}
                                imageToScreen={imageToScreen}
                                viewer={viewer}
                            />
                        )}
                        <ScribbleLayer
                            artboardOrigin={artboardOrigin}
                            scale={scale}
                            artboardSize={artboardSize}
                        >
                            {currentPath && <Path key="current" path={currentPath} />}
                            {scribble &&
                                scribble.scribblePaths.map(({ scribblePathCoords }, i) => {
                                    const path = scribblePathCoords.map(({ x, y }) => ({
                                        x,
                                        y,
                                    }));
                                    return <Path key={i} path={path} smooth />;
                                })}
                        </ScribbleLayer>
                    </FeedbackHandler>
                </Artboard>
            </ScrollPane>
        </FilePaneWrapper>
    );
}

export default FilePane;
